// admin/src/pages/Users/UserManagement.jsx
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Menu,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  People,
  Add,
  Edit,
  Delete,
  MoreVert,
  Search,
  FilterList,
  Download,
  WhatsApp,
  PersonAdd,
  Block,
  CheckCircle,
  Email,
  Phone,
  School,
  Home,
  Visibility,
  Person,
  PhotoCamera,
  CloudUpload,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { adminAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

const UserManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialRole = queryParams.get("role") || "";

  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState(initialRole);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(""); // 'view', 'edit', 'create'
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    role: "student",
    phoneNumber: "",
    password: "",
    studentId: "",
    aadharNumber: "",
    assignedRoomNumber: "",
    assignedBuilding: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
    },
    emergencyContact: {
      name: "",
      phone: "",
      relation: "",
    },
    specialization: "",
  });

  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    students: 0,
    wardens: 0,
    admins: 0,
  });

  const { user } = useAuth();

  useEffect(() => {
    const role = queryParams.get("role") || "";
    setRoleFilter(role);
    setPage(0);
  }, [location.search]);

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
    fetchRoomsList();
  }, [page, rowsPerPage, searchTerm, roleFilter, statusFilter]);

  const fetchRoomsList = async () => {
    try {
      const response = await adminAPI.getAllRooms();
      setRooms(response.data.rooms || []);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        role: roleFilter,
        isActive: statusFilter,
      };

      const response = await adminAPI.getAllUsers(params);
      setUsers(response.data.users || []);
      setTotalUsers(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to fetch users");
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleExportUsers = async () => {
    try {
      toast.info("Preparing export...");
      const params = {
        page: 1,
        limit: 10000, 
        search: searchTerm,
        role: roleFilter,
        isActive: statusFilter,
      };
      
      const response = await adminAPI.getAllUsers(params);
      const allUsers = response.data.users || [];
      
      if (allUsers.length === 0) {
        toast.info("No users to export");
        return;
      }

      const csvRows = [];
      csvRows.push(['Name', 'Email', 'Role', 'Status', 'Phone', 'Student ID', 'Aadhar', 'Joined Date'].join(','));
      
      allUsers.forEach(u => {
        csvRows.push([
          `"${u.name || ''}"`,
          `"${u.email || ''}"`,
          `"${u.role || ''}"`,
          `"${u.isActive ? 'Active' : 'Inactive'}"`,
          `"${u.phoneNumber || ''}"`,
          `"${u.studentId || ''}"`,
          `"${u.aadharNumber || ''}"`,
          `"${new Date(u.createdAt).toLocaleDateString()}"`
        ].join(','));
      });
      
      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', 'users_report.csv');
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await adminAPI.getUserStats();
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching user stats:", error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper to get profile picture URL from various formats
  const getProfilePicUrl = (userData) => {
    if (!userData?.profilePicture) return null;
    if (typeof userData.profilePicture === "string") return userData.profilePicture;
    if (userData.profilePicture?.url) return userData.profilePicture.url;
    return null;
  };

  const handlePhotoUpload = async (e, userId = null) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Image too large. Please use an image under 1.5MB.");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
    reader.readAsDataURL(file);
    const imageDataUrl = await base64Promise;

    // If we have a userId (editing existing user), upload immediately
    if (userId) {
      setPhotoUploading(true);
      try {
        await adminAPI.uploadUserAvatar(userId, { image: imageDataUrl });
        toast.success("Photo uploaded successfully!");
        setPhotoPreview(imageDataUrl);
        fetchUsers(); // Refresh the list
      } catch (error) {
        toast.error("Failed to upload photo");
        console.error("Photo upload error:", error);
      } finally {
        setPhotoUploading(false);
      }
    } else {
      // For new user creation, just preview (upload after user is created)
      setPhotoPreview(imageDataUrl);
    }
  };

  const handleOpenDialog = (type, userData = null) => {
    setDialogType(type);
    setSelectedUser(userData);
    setPhotoPreview(null); // Reset photo preview
    if (userData) {
      setUserForm({
        name: userData.name || "",
        email: userData.email || "",
        role: userData.role || "student",
        phoneNumber: userData.phoneNumber || "",
        password: "",
        studentId: userData.studentId || "",
        aadharNumber: userData.aadharNumber || "",
        assignedRoomNumber: userData.assignedRoomNumber || "",
        assignedBuilding: userData.assignedBuilding || "",
        address: {
          street: userData.address?.street || "",
          city: userData.address?.city || "",
          state: userData.address?.state || "",
          pincode: userData.address?.pincode || "",
        },
        emergencyContact: {
          name: userData.emergencyContact?.name || "",
          phone: userData.emergencyContact?.phone || "",
          relation: userData.emergencyContact?.relation || "",
        },
        specialization: userData.specialization || "",
      });
      if (type === 'view' && userData.role === 'student' && userData.studentId) {
        fetchStudentQR(userData.studentId);
      }
    } else {
      setQrCodeData(null);
      setUserForm({
        name: "",
        email: "",
        role: "student",
        phoneNumber: "",
        password: "",
        studentId: "",
        aadharNumber: "",
        assignedRoomNumber: "",
        assignedBuilding: "",
        address: {
          street: "",
          city: "",
          state: "",
          pincode: "",
        },
        emergencyContact: {
          name: "",
          phone: "",
          relation: "",
        },
        specialization: "",
      });
    }
    setOpenDialog(true);
  };

  const fetchStudentQR = async (studentId) => {
    setQrLoading(true);
    try {
      const response = await adminAPI.getStudentQR(studentId);
      setQrCodeData(response.data.qrCode);
    } catch (error) {
      console.error("Error fetching QR code:", error);
      setQrCodeData(null);
    } finally {
      setQrLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setDialogType("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setUserForm((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setUserForm({
        ...userForm,
        [name]: value,
      });
    }
  };

  const handleFormSubmit = async () => {
    try {
      if (!userForm.name || !userForm.email) {
        toast.error("Name and Email are required!");
        return;
      }

      setLoading(true);
      if (dialogType === "create") {
        const response = await adminAPI.createUser(userForm);
        const newUserId = response.data?.user?.id;
        toast.success("User created successfully!");

        // If a photo was selected during creation, upload it now
        if (photoPreview && newUserId) {
          try {
            await adminAPI.uploadUserAvatar(newUserId, { image: photoPreview });
            toast.success("Photo uploaded!");
          } catch (photoError) {
            console.error("Photo upload after create failed:", photoError);
            toast.warning("User created but photo upload failed. You can add it later.");
          }
        }
      } else {
        await adminAPI.updateUser(selectedUser._id || selectedUser.id, userForm);
        toast.success("User updated successfully!");
      }

      handleCloseDialog();
      fetchUsers();
      fetchUserStats();
    } catch (error) {
      console.error("SAVE ERROR:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      await adminAPI.toggleUserStatus(userId);
      toast.success("User status updated successfully");
      fetchUsers();
      fetchUserStats();
    } catch (error) {
      toast.error("Failed to update user status");
    }
    handleMenuClose();
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await adminAPI.deleteUser(userId);
        toast.success("User deleted successfully");
        fetchUsers();
        fetchUserStats();
      } catch (error) {
        toast.error("Failed to delete user");
      }
    }
    handleMenuClose();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "error";
      case "warden":
        return "warning";
      case "student":
        return "primary";
      default:
        return "default";
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? "success" : "error";
  };

  const StatCard = ({ title, value, icon, color = "primary" }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
              {value}
            </Typography>
            <Typography variant="h6">{title}</Typography>
          </Box>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && users.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #1a237e, #3949ab)",
          color: "white",
        }}
      >
        <Box 
          display="flex" 
          flexDirection={{ xs: "column", md: "row" }} 
          alignItems={{ xs: "flex-start", md: "center" }} 
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
              User Management
            </Typography>
            <Typography variant={isMobile ? "body1" : "h6"} sx={{ opacity: 0.9 }}>
              Manage students, staff, and admin accounts
            </Typography>
          </Box>
          <People sx={{ fontSize: isMobile ? 40 : 80, opacity: 0.7, alignSelf: isMobile ? "flex-end" : "center" }} />
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers || 0}
            icon={<People />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Active Users"
            value={stats.activeUsers || 0}
            icon={<CheckCircle />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Students"
            value={stats.students || 0}
            icon={<School />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Wardens"
            value={stats.wardens || 0}
            icon={<Person />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Admins"
            value={stats.admins || 0}
            icon={<Person />}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={2}
          mb={3}
        >
          <Typography variant="h6">All Users</Typography>
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Add />}
            onClick={() => handleOpenDialog("create")}
          >
            Add New User
          </Button>
        </Box>

        {/* Filters */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role Filter</InputLabel>
              <Select
                value={roleFilter}
                label="Role Filter"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="warden">Warden</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="staff">Staff</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportUsers}
            >
              Export
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Users Table */}
        <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Contact</TableCell>
                <TableCell>Role</TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Status</TableCell>
                <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>Room</TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Joined</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((userData) => (
                <TableRow key={userData._id || userData.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={getProfilePicUrl(userData)}
                        alt={userData.name}
                      >
                        {userData.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {userData.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {userData.email}
                        </Typography>
                        {userData.studentId && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            ID: {userData.studentId}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    <Box>
                      <Typography variant="body2">
                        📧 {userData.email}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        📞 {userData.phoneNumber || "N/A"}
                          <Tooltip title="Chat on WhatsApp">
                            <IconButton 
                              size="small" 
                              onClick={() => window.open(`https://wa.me/91${userData.phoneNumber ? userData.phoneNumber.replace(/[^0-9]/g, '') : ''}`, '_blank')}
                            >
                              <WhatsApp sx={{ color: '#25D366', fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={userData.role?.toUpperCase()}
                      color={getRoleColor(userData.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    <Chip
                      label={userData.isActive ? "Active" : "Inactive"}
                      color={getStatusColor(userData.isActive)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                    {userData.room ? (
                      <Chip
                        label={`Room ${userData.room.roomNumber}`}
                        color="info"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No Room
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    <Typography variant="body2">
                      {new Date(userData.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) =>
                        handleMenuOpen(e, userData._id || userData.id)
                      }
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {users.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <People sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No users found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || roleFilter || statusFilter
                ? "Try adjusting your filters"
                : "Add your first user to get started"}
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            const userData = users.find((u) => (u._id || u.id) === menuUserId);
            handleOpenDialog("view", userData);
            handleMenuClose();
          }}
        >
          <Visibility sx={{ mr: 2 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            const userData = users.find((u) => (u._id || u.id) === menuUserId);
            handleOpenDialog("edit", userData);
            handleMenuClose();
          }}
        >
          <Edit sx={{ mr: 2 }} />
          Edit User
        </MenuItem>
        <MenuItem onClick={() => handleToggleUserStatus(menuUserId)}>
          <Block sx={{ mr: 2 }} />
          Toggle Status
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteUser(menuUserId)}
          sx={{ color: "error.main" }}
        >
          <Delete sx={{ mr: 2 }} />
          Delete User
        </MenuItem>
      </Menu>

      {/* User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "view" && "User Details"}
          {dialogType === "edit" && "Edit User"}
          {dialogType === "create" && "Create New User"}
        </DialogTitle>
        <DialogContent>
          {selectedUser && dialogType === "view" && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <Avatar
                      src={photoPreview || getProfilePicUrl(selectedUser)}
                      sx={{ width: 100, height: 100, border: "3px solid", borderColor: "primary.main" }}
                    >
                      {selectedUser.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="view-photo-upload"
                      type="file"
                      onChange={(e) => handlePhotoUpload(e, selectedUser._id || selectedUser.id)}
                    />
                    <label htmlFor="view-photo-upload">
                      <IconButton
                        color="primary"
                        aria-label="upload picture"
                        component="span"
                        disabled={photoUploading}
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: -4,
                          bgcolor: "white",
                          boxShadow: 2,
                          width: 32,
                          height: 32,
                          "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                      >
                        {photoUploading ? <CircularProgress size={16} /> : <PhotoCamera sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </label>
                  </Box>
                  <Box>
                    <Typography variant="h6">{selectedUser.name}</Typography>
                    <Chip
                      label={selectedUser.role?.toUpperCase()}
                      color={getRoleColor(selectedUser.role)}
                      size="small"
                    />
                  </Box>
                </Box>
                <Typography variant="body2" gutterBottom>
                  <Email sx={{ mr: 1, fontSize: 16 }} />
                  {selectedUser.email}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <Phone sx={{ mr: 1, fontSize: 16 }} />
                  {selectedUser.phoneNumber || "N/A"}
                </Typography>
                {selectedUser.studentId && (
                  <Typography variant="body2" gutterBottom>
                    <School sx={{ mr: 1, fontSize: 16 }} />
                    Student ID: {selectedUser.studentId}
                  </Typography>
                )}
                {(selectedUser.role === "warden" || selectedUser.role === "admin") &&
                  (selectedUser.assignedRoomNumber || selectedUser.assignedBuilding) && (
                  <Box mt={1.5} p={1.5} sx={{ bgcolor: "warning.light", borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      🏢 Staff Assignment
                    </Typography>
                    {selectedUser.assignedRoomNumber && (
                      <Typography variant="body2">
                        Room: <strong>{selectedUser.assignedRoomNumber}</strong>
                      </Typography>
                    )}
                    {selectedUser.assignedBuilding && (
                      <Typography variant="body2">
                        Building: <strong>{selectedUser.assignedBuilding}</strong>
                      </Typography>
                    )}
                  </Box>
                )}
                {selectedUser.role === "student" && selectedUser.room && (
                  <Box mt={1.5} p={1.5} sx={{ bgcolor: "info.light", color: "info.contrastText", borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      🏠 Stay Information
                    </Typography>
                    <Typography variant="body2">
                      Room: <strong>{selectedUser.room.roomNumber}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Building: <strong>{selectedUser.room.building}</strong>
                    </Typography>
                  </Box>
                )}
                {selectedUser.role === "staff" && selectedUser.specialization && (
                  <Box mt={1.5} p={1.5} sx={{ bgcolor: "success.light", color: "success.contrastText", borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      🔧 Staff Specialization
                    </Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {selectedUser.specialization}
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Address
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedUser.address?.street &&
                    `${selectedUser.address.street}, `}
                  {selectedUser.address?.city &&
                    `${selectedUser.address.city}, `}
                  {selectedUser.address?.state &&
                    `${selectedUser.address.state} `}
                  {selectedUser.address?.pincode &&
                    `${selectedUser.address.pincode}`}
                  {!selectedUser.address?.city && "Address not provided"}
                </Typography>

                {selectedUser.emergencyContact && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Emergency Contact
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.emergencyContact.name} (
                      {selectedUser.emergencyContact.relation})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      📞 {selectedUser.emergencyContact.phone}
                    </Typography>
                  </Box>
                )}
                {selectedUser.aadharNumber && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Aadhar Number
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.aadharNumber}
                    </Typography>
                  </Box>
                )}
                {selectedUser.role === "student" && (
                  <Box mt={2} textAlign="center">
                    <Typography variant="subtitle2" gutterBottom align="left">
                      Digital Key (QR)
                    </Typography>
                    {qrLoading ? (
                      <CircularProgress size={24} />
                    ) : qrCodeData ? (
                      <img src={qrCodeData} alt="QR Code" style={{ width: 150, height: 150, border: '1px solid #ddd', borderRadius: 8 }} />
                    ) : (
                      <Typography variant="caption" color="error">QR not available</Typography>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {(dialogType === "create" || dialogType === "edit") && (
            <Box component="form" sx={{ mt: 2 }}>
              {/* Photo Upload Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 3,
                  p: 3,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "grey.300",
                }}
              >
                <Avatar
                  src={
                    photoPreview ||
                    (selectedUser ? getProfilePicUrl(selectedUser) : null)
                  }
                  sx={{
                    width: 100,
                    height: 100,
                    mb: 2,
                    border: "3px solid",
                    borderColor: "primary.main",
                    fontSize: 40,
                  }}
                >
                  {userForm.name?.charAt(0)?.toUpperCase() || "?"}
                </Avatar>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="form-photo-upload"
                  type="file"
                  onChange={(e) =>
                    handlePhotoUpload(
                      e,
                      dialogType === "edit"
                        ? selectedUser?._id || selectedUser?.id
                        : null
                    )
                  }
                />
                <label htmlFor="form-photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={
                      photoUploading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CloudUpload />
                      )
                    }
                    disabled={photoUploading}
                    size="small"
                  >
                    {photoPreview
                      ? "Change Photo"
                      : selectedUser?.profilePicture
                      ? "Update Photo"
                      : "Upload Photo"}
                  </Button>
                </label>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  JPG, PNG or GIF • Max 1.5MB
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    required
                    value={userForm.name}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    required
                    value={userForm.email}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Role</InputLabel>
                    <Select
                      name="role"
                      value={userForm.role}
                      label="Role"
                      onChange={handleFormChange}
                    >
                      <MenuItem value="student">Student</MenuItem>
                      <MenuItem value="warden">Warden</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="staff">Staff</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {userForm.role === "student" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Student ID"
                      name="studentId"
                      value={userForm.studentId}
                      onChange={handleFormChange}
                      placeholder="Auto-generated if left blank"
                    />
                  </Grid>
                )}
                {userForm.role === "staff" && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Specialization</InputLabel>
                      <Select
                        name="specialization"
                        value={userForm.specialization}
                        label="Specialization"
                        onChange={handleFormChange}
                      >
                        <MenuItem value="electrical">Electrical</MenuItem>
                        <MenuItem value="plumbing">Plumbing</MenuItem>
                        <MenuItem value="maintenance">Maintenance</MenuItem>
                        <MenuItem value="cleaning">Cleaning</MenuItem>
                        <MenuItem value="security">Security</MenuItem>
                        <MenuItem value="wifi">WiFi</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    value={userForm.phoneNumber}
                    onChange={handleFormChange}
                  />
                </Grid>
                {(dialogType === "create" || dialogType === "edit") && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={dialogType === "create" ? "Password" : "New Password (leave blank to keep current)"}
                      name="password"
                      type="password"
                      required={dialogType === "create"}
                      value={userForm.password}
                      onChange={handleFormChange}
                      placeholder={dialogType === "edit" ? "Enter new password only if you want to reset it" : ""}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Aadhar Number"
                    name="aadharNumber"
                    value={userForm.aadharNumber}
                    onChange={handleFormChange}
                    placeholder="12-digit Aadhar Number"
                  />
                </Grid>
                {(userForm.role === "warden" || userForm.role === "admin" || userForm.role === "student") && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Assigned Building</InputLabel>
                        <Select
                          name="assignedBuilding"
                          value={userForm.assignedBuilding || ""}
                          label="Assigned Building"
                          onChange={(e) => {
                            setUserForm({ ...userForm, assignedBuilding: e.target.value, assignedRoomNumber: "" });
                          }}
                        >
                          {Array.from(new Set(
                            rooms
                              .filter(r => userForm.role !== 'student' || r.status === 'available')
                              .map(r => r.building)
                          )).filter(Boolean).map((building) => (
                            <MenuItem key={building} value={building}>{building}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth disabled={!userForm.assignedBuilding}>
                        <InputLabel>Assigned Room Number</InputLabel>
                        <Select
                          name="assignedRoomNumber"
                          value={userForm.assignedRoomNumber || ""}
                          label="Assigned Room Number"
                          onChange={handleFormChange}
                        >
                          {rooms
                            .filter(r => r.building === userForm.assignedBuilding)
                            .filter(r => userForm.role !== 'student' || r.status === 'available')
                            .map((r) => (
                              <MenuItem key={r.roomNumber} value={r.roomNumber}>{r.roomNumber}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Student/Staff ID"
                    name="studentId"
                    value={userForm.studentId}
                    onChange={handleFormChange}
                    placeholder="Leave blank to auto-generate"
                    helperText={dialogType === 'create' ? "Will auto-generate (e.g. HI-ST-001) if blank" : ""}
                  />
                </Grid>

                {/* Address Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    Address Details
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    name="address.street"
                    value={userForm.address.street}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    name="address.city"
                    value={userForm.address.city}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    name="address.state"
                    value={userForm.address.state}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    name="address.pincode"
                    value={userForm.address.pincode}
                    onChange={handleFormChange}
                  />
                </Grid>

                {/* Emergency Contact Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    Emergency Contact
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    name="emergencyContact.name"
                    value={userForm.emergencyContact.name}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Contact Phone"
                    name="emergencyContact.phone"
                    value={userForm.emergencyContact.phone}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Relation</InputLabel>
                    <Select
                      name="emergencyContact.relation"
                      value={userForm.emergencyContact.relation}
                      label="Relation"
                      onChange={handleFormChange}
                    >
                      <MenuItem value="parent">Parent</MenuItem>
                      <MenuItem value="guardian">Guardian</MenuItem>
                      <MenuItem value="sibling">Sibling</MenuItem>
                      <MenuItem value="relative">Relative</MenuItem>
                      <MenuItem value="friend">Friend</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {dialogType !== "view" && (
            <Button
              variant="contained"
              onClick={handleFormSubmit}
              disabled={loading}
            >
              {dialogType === "create" ? "Add User" : "Save Changes"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
